/**
 * The format rules for a buyer's tax id, with no dependencies: the payments
 * service, its SDK and the payments frontend all check against these, so a
 * number the form accepts is one the server accepts (#172). Whether the number
 * is REAL is the registry's call (tax-identity.ts), not this module's.
 */

/** The caller's claim: a country and a number. "PL" means a NIP. */
export interface TaxIdInput {
  readonly countryCode: string;
  readonly number: string;
}

/** Polish NIP: 10 digits, not all identical, weights 6 5 7 2 3 4 5 6 7 mod 11 (control 10 invalid). */
export function isValidNip(nip: string): boolean {
  if (!/^\d{10}$/.test(nip) || /^(\d)\1{9}$/.test(nip)) {
    return false;
  }
  const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
  const control = weights.reduce((acc, w, i) => acc + w * Number(nip[i]), 0) % 11;
  return control !== 10 && control === Number(nip[9]);
}

/**
 * Normalise the caller's claim. A NIP may come as "PL 526-025-02-74" and a VAT
 * number with its own country prefix; "GR" is accepted for Greece (VIES says EL).
 */
export function normaliseTaxIdInput(input: TaxIdInput): TaxIdInput {
  const raw = input.countryCode.trim().toUpperCase();
  const countryCode = raw === "GR" ? "EL" : raw;
  let number = input.number.toUpperCase().replace(/[\s.\-/]/g, "");
  if (number.startsWith(countryCode) || (countryCode === "EL" && number.startsWith("GR"))) {
    number = number.slice(2);
  }
  return { countryCode, number };
}
